import { useRouter } from "next/router";
import { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import getConfig from "next/config";
import classNames from "classnames";

import { useOnScreen } from "@lib/useOnScreen";
import ProductPageHeaderRatings from "@components/ProductPage/ProductPageHeaderRatings";
import Ratings from "@components/Ratings";
import NoRatingsDataPlaceholder from "@components/Ratings/NoRatingsDataPlaceholder";
import ProductLabels from "@components/ProductPage/ProductLabels";
import ProductMedia from "@components/ProductMedia";
import ProductDescription from "@components/ProductPage/ProductDescription";
import Specifications from "@components/Specifications";
import ProductBreadcrumbs from "@components/ProductPage/ProductBreadcrumbs";
import Price from "@components/Price";
import Status from "@components/Status";
import ProductLabel from "@components/ProductPage/ProductLabel";
import ProductVariations from "@components/ProductPage/ProductVariations";
import AddToCart from "@containers/Catalog/Actions/AddToCart";
import ProductEnergyClass from "@components/ProductPage/ProductEnergyClass";
import ProductEnergyLabelModal from "@components/ProductPage/ProductEnergyLabelModal";
import ProductServices from "@components/ProductPage/ProductServices";
import ProductBenefits from "@components/ProductPage/ProductBenefits";
import ReviewsContainer from "@containers/Reviews/index";
import CompareWidget from "@containers/Catalog/CompareWidget";
import ProductCompareSection from "@containers/Catalog/ProductCompareSection";
import ProductsRecentlyViewed from "@components/ProductsRecentlyViewed";
import { addProductToProductNavigationHistory } from "@store/catalog/actions";
import { addModal } from "@store/app/actions";
import { trackProductClick } from "@store/tracker/actions";
import { apiCall } from "@store/app/actions";
import Westaco from "@components/ProductPage/Westaco";
import ResealedList from "@components/ProductPage/ResealedList";
import Bundles from "@containers/Catalog/Bundles";
import Heading from "@components/Heading";
import ExpandableProductList from "@components/ExpandableProductList";
import ProductOffers from "@components/ProductOffers";
import ProductPageStickyHeader from "@components/ProductPage/ProductPageStickyHeader";
import ProductLabelDescription from "@components/ProductLabelDescription";
import PricePerUnit from "@components/Price/PricePerUnit";
import ProductWarranty from "@components/ProductWarranty";
import ProductGreenTax from "@components/ProductGreenTax";
import {
  productHelper,
  resizeImage,
  getSlug,
  getProductAttributeByCode,
} from "@lib/helpers";
import {
  getProductShippingCostApi,
  getProductRecommendationsApi,
  getProductBundlesApi,
  getLoanSimulationApi,
} from "@lib/api";
import Alert from "@components/Alert";
import ProductButtonViewResealed from "@components/ProductButtonViewResealed";
import Accordion from "@components/Accordion";
import AccordionItem from "@components/Accordion/Item";
import AccordionItemHeader from "@components/Accordion/Item/Header";
import AccordionItemBody from "@components/Accordion/Item/Body";
import ProductStoreAvailability from "@components/ProductPage/ProductStoreAvailability";
import VivinoWidget from "@components/ProductPage/VivinoWidget";
import Tabs, { Tab } from "@components/Tabs";
import ProductPageStickyFooter from "@components/ProductPage/ProductPageStickyFooter";
import ProductBrand from "@components/ProductPage/ProductBrand";
import {
  setGTMProductOpenReviewsSection,
  setGTMProductsImpressions,
} from "@lib/GTMHelper";
import FreeShippingPromoMessage from "@components/ProductPage/FreeShippingPromoMessage";
import ProductFinancing from "@components/ProductFinancing";
import CredexUserPromoBanner from "@components/CredexUserPromoBanner";
import ProductPageSellerInfo from "@components/ProductPage/ProductPageSellerInfo";
import ProductPageResealedInfo from "@components/ProductPage/ProductPageResealedInfo";
import ProductServicesModal from "@components/ProductPage/ProductServices/ProductServicesModal";
import ArrowDown from "@components/Icons/ArrowDown";
import ImageWithFallback from "@components/ImageWithFallback";
import ProductPageWishlistAndCompare from "@components/ProductPage/ProductPageWishlistAndCompare";
import ProductLockerAvailability from "@components/ProductPage/ProductLockerAvailability";
import ProductPageShipping from "@components/ProductPage/ProductPageShipping";
import CredexCashbackBanner from "@components/CredexCashbackBanner";
import { localClientStore } from "@lib/clientStore";
import { getAddresses } from "@store/user/actions";
import replaceDiacriticsWithBaseCharacter from "@lib/replaceDiacriticsWithBaseCharacter";

const {
  publicRuntimeConfig: { settings, oney, credex },
} = getConfig();

const CatalogView = ({
  currentProduct,
  addModal,
  user,
  trackProductClick,
  addProductToProductNavigationHistory,
  cart,
  apiCall,
  credexConfig,
  getAddresses,
}) => {
  const router = useRouter();
  const galleryRef = useRef();
  const addToCartRef = useRef();
  const bundlesRef = useRef();

  const [openedAccordionItems, setOpenedAccordionItems] = useState([]);
  const [activeSection, setActiveSection] = useState("");
  const [activeServices, setActiveServices] = useState({});
  const [westacoData, setWestacoData] = useState(null);
  const [westacoValid, setWestacoValid] = useState({});
  const [productRecommendations, setProductRecommendations] = useState();
  const [shippingTax, setShippingTax] = useState({
    tax_total: null,
    sgr_tax: null,
  });
  const [productBundles, setProductBundles] = useState(null);
  const [loanSimulation, setLoanSimulation] = useState();
  const [showBenefits, setShowBenefits] = useState(false);

  const isStickyHeaderVisible = useOnScreen({
    ref: galleryRef,
    rootMargin: "-300px",
  });

  const isStickyFooterVisible = useOnScreen({
    ref: addToCartRef,
    rootMargin: "0px",
  });

  const product = currentProduct.product;

  const { customer, favouriteLockerId } = user;

  const selectedLockerId =
    customer?.locker_id ||
    favouriteLockerId ||
    localClientStore.get("favourite_locker_id");

  const {
    shipping_info: shippingInfo,
    sgr_tax: sgrTax,
    has_freeshipping_promo: hasFreeShippingPromo,
  } = shippingTax;

  const {
    isSaleable,
    hasStorePickup,
    isResealed,
    hasResealed,
    isVirtualService,
    isEol,
    isVisible,
    isWestaco,
    isLocalSeller,
    mainOffer,
    hasOtherOffers,
    otherOffers,
    hasPriceUnit,
    isVirtualProduct,
    getWarranty,
    hasWarranty,
    productNumber,
    hasPricePerFactor,
  } = productHelper(product);

  const vivinoId = getProductAttributeByCode(product, "vivino_id")?.value;

  const reviewsCount =
    currentProduct.reviews_summary?.items?.[0]?.reviews?.value ?? 0;
  const reviewsScore =
    currentProduct.reviews_summary?.items?.[0]?.reviews?.total ?? 0;
  const questionsCount =
    currentProduct.reviews_summary?.items?.[0]?.questions?.total ?? 0;
  const reviewAverage =
    Math.round((reviewsScore / reviewsCount + Number.EPSILON) * 100) / 100;

  const mainOfferSellerId = mainOffer?.seller?.id;

  const getIsInSpecialCampaign = (sellerId, product) => {
    return (
      sellerId &&
      product?.in_special_campaign_sellers?.length > 0 &&
      product.in_special_campaign_sellers.includes(sellerId)
    );
  };

  const isInSpecialCampaign = getIsInSpecialCampaign(
    mainOfferSellerId,
    product
  );

  const getLabelsBySellerId = (sellerId, product) => {
    if (!product?.label_actions_serialized?.[0]) {
      return [];
    }

    return product.label_actions_serialized.filter((label) =>
      label?.seller_ids.includes(sellerId)
    );
  };

  const labels = getLabelsBySellerId(mainOfferSellerId, product);

  const hasLabels = labels?.[0];
  const hasBenefits = labels?.length > 0;

  const hasCredexUserPromo = customer?.has_promo === "CC12RATE";

  const hasServices =
    (isSaleable || hasStorePickup) &&
    isLocalSeller &&
    Object.keys(product.product_links ?? {}).length > 0;

  const hasFinancingOptions = Boolean(
    product.installments_price > 0 &&
      ((credex.isEnabled && !isVirtualService) || oney.isEnabled)
  );

  const hasGreenTax =
    !isEol && isVisible && isLocalSeller && mainOffer?.price?.green_tax > 0;

  const hasBundles = productBundles?.bundles?.length > 0;

  const hasProductVariations = product.configurable_options?.length > 0;
  const hasProductAccesories =
    productRecommendations?.cross_sell_tabs?.length > 0;
  const warranty = getWarranty();

  const productDescriptionCMSBlocks =
    product.label_actions_serialized?.[0]?.cmsBLock;

  const hasEnergyClass = Boolean(product.energy_class);

  const hasEnergyLabel =
    product.energy_class &&
    product.energy_class_image &&
    !product.energy_class_image.includes("no_selection");

  const installmentValue =
    mainOffer &&
    credexConfig &&
    Number(mainOffer.price.selling_price / credexConfig.promoRates).toFixed(2);

  const isLockerEligible =
    Boolean(product.locker_eligible) &&
    !(isWestaco || isVirtualProduct || isVirtualService);

  const serviceClick = ({ serviceKey, sku, additional_data }) => {
    if (activeServices[serviceKey] && activeServices[serviceKey].sku === sku) {
      const current_additional_data =
        activeServices[serviceKey]?.additional_data;
      if (additional_data?.childSku) {
        if (
          current_additional_data?.childSku &&
          current_additional_data?.childSku === additional_data.childSku
        ) {
          const tempActiveServices = { ...activeServices };
          delete tempActiveServices[serviceKey].additional_data.childSku;
          setActiveServices(tempActiveServices);
        } else {
          const tempActiveServices = {
            ...activeServices,
            [serviceKey]: {
              sku,
              additional_data,
            },
          };

          setActiveServices(tempActiveServices);
        }
      } else {
        const tempActiveServices = { ...activeServices };
        delete tempActiveServices[serviceKey];
        setActiveServices(tempActiveServices);
      }
    } else {
      const tempActiveServices = {
        ...activeServices,
        [serviceKey]: {
          sku,
          additional_data,
        },
      };

      setActiveServices(tempActiveServices);
    }
  };

  const removeService = ({ key, child }) => {
    const tempActiveServices = { ...activeServices };
    if (child) {
      delete tempActiveServices[key].additional_data;
    } else {
      delete tempActiveServices[key];
    }

    setActiveServices(tempActiveServices);
  };

  const toggleBenefits = (e) => {
    e.preventDefault();
    setShowBenefits((prev) => !prev);
  };

  const westacoFormCb = (data) => {
    setWestacoData(data.values);
    setWestacoValid(Object.keys(data.errors).length ? false : true);
  };

  const handleProductClick = (data, options) => {
    // Track product click
    trackProductClick(data, options);
  };

  const handleEnergyClassClick = (event) => {
    event.preventDefault();
    addModal("productEnergyLabel");
  };

  const getProductLoanSimulation = async () => {
    let methods = [];

    if (oney.isEnabled && oney.showInfoInProductPage)
      methods.push("oney_facilypay");

    if (isSaleable && !isVirtualService && mainOffer) methods.push("credex");

    if (!isEol && isVisible && mainOffer) {
      const response = await apiCall({
        apiCall: getLoanSimulationApi,
        apiArgs: [
          {
            amount: mainOffer.price?.selling_price,
            method: methods,
            sku: product.sku,
          },
        ],
        formatResponse: (response) => response.data.data,
        returnResponse: true,
      });

      if (response && !response.error) {
        setLoanSimulation(response);
      }
    }
  };

  const handleGTMProductImpressions = (productData) => {
    if (productData.cross_sell_tabs?.length > 0) {
      productData.cross_sell_tabs.forEach((tab) => {
        if (tab?.products?.length > 0) {
          const newProductFormat = tab.products.map((product, index) => ({
            ...product,
            index: index + 1,
            item_list_name: "accesorii",
          }));

          setGTMProductsImpressions(newProductFormat);
        }
      });
    }

    if (productData.up_sell?.length > 0) {
      const newProductFormat = productData.up_sell.map((product, index) => ({
        ...product,
        index: index + 1,
        item_list_name: "produse similare",
      }));

      setGTMProductsImpressions(newProductFormat);
    }

    if (productData.also_bought?.length > 0) {
      const newProductFormat = productData.also_bought.map(
        (product, index) => ({
          ...product,
          index: index + 1,
          item_list_name: "alti vizitatori au cumparat",
        })
      );

      setGTMProductsImpressions(newProductFormat);
    }

    if (productData.bought_together?.length > 0) {
      const newProductFormat = productData.bought_together.map(
        (product, index) => ({
          ...product,
          index: index + 1,
          item_list_name: "cumparate frecvent impreuna",
        })
      );

      setGTMProductsImpressions(newProductFormat);
    }
  };

  const setSection = (section, options = {}) => {
    const { scroll = false, hashChange = false, expand = false } = options;

    if (scroll) {
      const sectionElement = document.getElementById(section);

      if (sectionElement) {
        let newOpenedAccordionItems = [...openedAccordionItems, section];

        // Close description before scrolling because it can contain
        // dynamic content
        if (
          section !== "description" &&
          openedAccordionItems.includes("description")
        ) {
          newOpenedAccordionItems = newOpenedAccordionItems.filter(
            (item) => item !== "description"
          );

          setOpenedAccordionItems(newOpenedAccordionItems);

          // Scroll after animations are done
          setTimeout(() => {
            const stickyHeaderOffset = innerWidth >= 640 ? 120 + 20 : 0;

            const offsetPosition =
              sectionElement.getBoundingClientRect().top +
              window.scrollY -
              stickyHeaderOffset;

            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth",
            });
          }, 350);
        } else {
          setOpenedAccordionItems(newOpenedAccordionItems);

          const stickyHeaderOffset = innerWidth >= 640 ? 120 + 20 : 0;

          const offsetPosition =
            sectionElement.getBoundingClientRect().top +
            window.scrollY -
            stickyHeaderOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }
    } else {
      if (expand) {
        if (!openedAccordionItems.includes(section)) {
          setOpenedAccordionItems([...openedAccordionItems, section]);
        }
      } else {
        setOpenedAccordionItems(
          openedAccordionItems.filter((item) => item !== section)
        );
      }
    }

    if (hashChange) {
      // Change hash without re-render or scroll
      history.replaceState({}, "", `#${expand ? section : ""}`);
    }

    setActiveSection(section);
  };

  const onSectionClick = (section) => {
    setSection(section, { scroll: true, hashChange: true, expand: true });

    if (section === "reviews") {
      setGTMProductOpenReviewsSection(product.sku);
    }
  };

  const onAccordionItemToggle = ({ item, expanded }) =>
    setSection(item, { scroll: false, hashChange: true, expand: expanded });

  const onHeaderRatingsClick = (event) => {
    event.preventDefault();
    setSection("reviews", { scroll: true, hashChange: true, expand: true });
    setGTMProductOpenReviewsSection(product.sku);
  };

  const onResealedClick = (event) => {
    event.preventDefault();
    setSection("resigilate", { scroll: true, hashChange: true, expand: true });
  };

  useEffect(() => {
    const getShippingTax = async () => {
      const attributes =
        product.info?.groups?.reduce(
          (attributesAcc, group) => ({
            ...attributesAcc,
            ...group.attributes?.reduce(
              (groupAttributesAcc, attribute) =>
                attribute.attribute_code
                  ? {
                      ...groupAttributesAcc,
                      [attribute.attribute_code]: attribute.value,
                    }
                  : groupAttributesAcc,
              {}
            ),
          }),
          {}
        ) ?? {};

      let city = "Bucuresti";
      let region = "Bucuresti";

      if (
        cart?.miniCart?.shipping_address?.city &&
        cart?.miniCart?.shipping_address?.region
      ) {
        city = cart.miniCart.shipping_address.city;
        region = cart.miniCart.shipping_address.region;
      } else {
        if (user.isLoggedIn) {
          let defaultShippingAddress;
          let fallbackAddress;

          if (user.addresses?.length > 0) {
            defaultShippingAddress = user.addresses.find(
              (address) => address.default_shipping
            );

            fallbackAddress = user.addresses.find(
              (address) => address.city && address.region
            );
          } else {
            const addresses = await getAddresses();

            if (addresses && addresses.length) {
              defaultShippingAddress = addresses.find(
                (address) => address.default_shipping
              );

              fallbackAddress = addresses.find(
                (address) => address.city && address.region
              );
            }
          }

          if (defaultShippingAddress || fallbackAddress) {
            if (defaultShippingAddress?.city || fallbackAddress?.city) {
              city = defaultShippingAddress?.city || fallbackAddress.city;
            } else if (
              defaultShippingAddress?.region ||
              fallbackAddress?.region
            ) {
              region = defaultShippingAddress?.region || fallbackAddress.region;
            }
          }
        }
      }

      const response = await apiCall({
        apiArgs: [
          {
            sku: product.sku,
            seller_id: mainOffer.seller.id,
            selling_price: mainOffer.price.selling_price,
            attribute_set_id: product.attribute_set_id,
            // TODO: Some products don't have brands,
            // we should discuss with backend and see what payload
            // we should send
            brand: product.brand,
            weight: attributes.greutate_kg_global ?? 0,
            city: replaceDiacriticsWithBaseCharacter(city),
            region: replaceDiacriticsWithBaseCharacter(region),
            televizoare_dimensiune_ecran:
              attributes.televizoare_dimensiune_ecran ?? 0,
            ...(attributes.bauturi_cantitate_bax && { get_sgr: 1 }),
            ...(isLockerEligible &&
              selectedLockerId && { locker_id: selectedLockerId }),
          },
        ],
        apiCall: getProductShippingCostApi,
        formatResponse: (response) => response.data,
        returnResponse: true,
      });

      if (response) {
        setShippingTax(response);
      }
    };

    if (
      !cart.miniCartLoading &&
      mainOffer &&
      isSaleable &&
      !isVirtualProduct &&
      !isWestaco
    ) {
      getShippingTax();
    }
  }, [
    apiCall,
    cart.miniCartLoading,
    product,
    mainOffer,
    isSaleable,
    isVirtualProduct,
    isWestaco,
    selectedLockerId,
  ]);

  useEffect(() => {
    const init = async () => {
      addProductToProductNavigationHistory({
        id: product.id,
        sku: product.sku,
        name: product.name,
        image: resizeImage(product.small_image, "300x300"),
        route: `${settings.baseUrl}/${product.url_key}/cpd/${product.sku}/`,
        isNavigationHistory: true,
      });

      const hash = router.asPath.split("#")[1];

      if (hasCredexUserPromo && !loanSimulation) {
        await getProductLoanSimulation();
      }

      if (hash) {
        const section = getSlug(hash);
        const sectionElement = document.getElementById(section);

        if (sectionElement) {
          setSection(section, { scroll: true, expand: true });
        } else {
          setSection("description", { scroll: false, expand: true });
        }
      } else {
        setSection("description", { scroll: false, expand: true });
      }
    };

    init();
  }, []);

  useEffect(() => {
    const getProductRecommendations = async () => {
      const response = await apiCall({
        apiArgs: [{ sku: product.sku }],
        apiCall: getProductRecommendationsApi,
        formatResponse: (response) => response.data,
        returnResponse: true,
      });

      if (response?.data) {
        setProductRecommendations(response.data);
        handleGTMProductImpressions(response.data);
      }
    };

    getProductRecommendations();
  }, []);

  useEffect(() => {
    const getProductBundles = async () => {
      const response = await apiCall({
        apiArgs: [{ sku: product.sku }],
        apiCall: getProductBundlesApi,
        formatResponse: (response) => response.data,
        returnResponse: true,
      });

      if (response?.data) {
        setProductBundles(response.data);
      }
    };

    if (settings.websiteName !== "bricostore") {
      getProductBundles();
    }
  }, []);

  // Scrollspy: auto-mark the section currently in view in the sticky nav (bricostore only,
  // to keep altex/mediagalaxy behavior unchanged).
  useEffect(() => {
    if (settings.websiteName !== "bricostore") return;
    if (typeof IntersectionObserver === "undefined") return;

    const sectionIds = [
      "oferte",
      "pachete",
      "resigilate",
      "accessories",
      "description",
      "additional",
      "reviews",
      "questions",
      "compare",
    ];

    const elements = sectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // pick the intersecting section closest to the top of the active band
        const topMost = entries
          .filter((entry) => entry.isIntersecting)
          .sort(
            (a, b) => a.boundingClientRect.top - b.boundingClientRect.top
          )[0];

        if (topMost) {
          setActiveSection(topMost.target.id);
        }
      },
      // offset for the ~120px sticky header; a section is "current" once its
      // top passes just under the bar (top band ~140px..45% of viewport)
      { rootMargin: "-140px 0px -55% 0px", threshold: 0 }
    );

    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [
    product?.sku,
    hasOtherOffers,
    hasBundles,
    hasResealed,
    hasProductAccesories,
  ]);

  return (
    <div className="relative">
      <ProductPageStickyHeader
        isVisible={isStickyHeaderVisible}
        product={product}
        reviews={currentProduct.reviews_summary}
        sectionInView={
          settings.websiteName === "bricostore" ? activeSection : undefined
        }
        onSectionClick={onSectionClick}
        westacoData={westacoData}
        isWestacoDataValid={westacoValid}
        activeServices={activeServices}
        hasProductAccesories={hasProductAccesories}
        hasBundles={hasBundles}
      />

      {!isEol && isVisible && (isSaleable || hasStorePickup) && (
        <div
          className={`fixed block md:hidden bottom-0 left-0 right-0 z-[900] bg-white px-[15px] pt-[10px] transition-transform ease-in-out ${
            isStickyFooterVisible ? "translate-y-[66.8px]" : "translate-y-0"
          }`}
        >
          <ProductPageStickyFooter
            product={product}
            westacoData={westacoData}
            isWestacoDataValid={westacoValid}
            activeServices={activeServices}
          />
        </div>
      )}

      <ProductBreadcrumbs
        breadcrumbs={currentProduct?.breadcrumbs}
        sku={product.sku}
      />

      {hasFreeShippingPromo && (
        <div className="mb-4">
          <FreeShippingPromoMessage />
        </div>
      )}

      {/* Product name and ratings */}
      <div className="mb-5 flex flex-col-reverse md:flex-col gap-[16px] md:gap-[0]">
        <Heading
          as="h1"
          display="h1"
          font="normal"
          classesProp="!text-[20px] md:!text-[24px] !leading-26 md:!leading-36"
          title={
            <>
              {isResealed && <span>Resigilat: </span>}
              {product.name}
            </>
          }
        />

        <div className="flex flex-wrap items-center mt-1 gap-2 md:gap-0">
          {!isWestaco &&
            (reviewsCount > 0 ? (
              <ProductPageHeaderRatings
                reviewsCount={reviewsCount}
                ratingSummary={reviewsScore}
                productUrl={`/${product.url_key}/cpd/${product.sku}/`}
                onClick={onHeaderRatingsClick}
              />
            ) : (
              <NoRatingsDataPlaceholder
                onClick={onHeaderRatingsClick}
                productUrl={`/${product.url_key}/cpd/${product.sku}/`}
              />
            ))}
        </div>
      </div>

      {/* BF label */}
      {isInSpecialCampaign && (
        <img
          src={`${settings.websiteStatics}/images/oferta-de-black-friday.svg`}
          alt="oferta de black friday"
          className="w-[124px]"
        />
      )}

      {/* Main product view */}
      <div
        className="flex items-start flex-col md:flex-row space-y-[16px] md:space-y-0 md:space-x-[48px] justify-center xl:justify-between mb-[32px]"
        data-test="MAIN_PRODUCT_VIEW"
      >
        <div
          className="relative md:sticky md:top-0 w-full md:overflow-hidden space-y-[16px] xl:max-w-[45%]"
          ref={galleryRef}
          id="gallery"
        >
          <div className="md:max-h-120">
            <ProductMedia product={product} />
          </div>

          {product.brand_link && product.brand_logo && product.brand_name && (
            <ProductBrand product={product} />
          )}

          {vivinoId && (
            <div className="absolute top-0 right-[10px] z-[1]">
              <VivinoWidget vivinoId={vivinoId} />
            </div>
          )}

          {hasLabels && <ProductLabels labels={labels[0]} />}

          {hasEnergyClass && (
            <>
              <div className="absolute right-0 top-2 z-10">
                <ProductEnergyClass
                  energyClass={product.energy_class}
                  isClickable={hasEnergyLabel}
                  onClick={handleEnergyClassClick}
                  width="80px"
                />
              </div>

              {hasEnergyLabel && (
                <ProductEnergyLabelModal
                  productName={product.name}
                  labelPath={product.energy_class_image}
                />
              )}
            </>
          )}

          {productDescriptionCMSBlocks?.[
            "altex_label/rule_action_type_cmsBlock"
          ]?.content && (
            <div className="px-4 md:px-10 md:mb-10">
              <ProductLabelDescription
                cmsBlock={
                  productDescriptionCMSBlocks[
                    "altex_label/rule_action_type_cmsBlock"
                  ].content
                }
              />
            </div>
          )}
        </div>

        {/* product details */}
        <div className="w-full xl:max-w-[45%]">
          {isEol && (
            <div className="col-span-2 w-full mb-3 lg:px-3">
              <Alert type="info" showIcon>
                Produsul nu mai face parte din catalogul magazinului.
              </Alert>
            </div>
          )}

          {!isEol && !isVisible && (
            <div className="col-span-2 w-full mb-3 lg:px-3">
              <Alert type="info" showIcon>
                Acest produs nu poate fi adaugat in cos individual.
              </Alert>
            </div>
          )}

          {!isEol && isVisible && (
            <>
              <div className="space-y-[16px]">
                {/* price and status */}
                <div className="flex flex-col md:flex-row gap-[16px]">
                  {mainOffer && (
                    <div className="lg:w-3/5 flex flex-col w-full">
                      {isWestaco && <div>De la</div>}

                      {product.discount_type === "repositioning" &&
                        isSaleable && (
                          <ProductLabel label="super pret" id="super-pret" />
                        )}

                      <Price
                        productId={parseInt(product.id)}
                        price={mainOffer.price.selling_price}
                        regularPrice={mainOffer.price.base_price}
                        msrp={mainOffer.price.msrp_price}
                        showMSRPTooltip={true}
                        formatted={true}
                        size="productPage"
                        discountType={mainOffer.price.discount_type}
                        displayDiscount={true}
                        displayUnit={
                          hasPricePerFactor
                            ? product.price_unit.split("/")[1]
                            : false
                        }
                      />

                      {hasPriceUnit &&
                        (mainOffer?.price?.selling_price ||
                          mainOffer?.price?.package_selling_price) && (
                          <PricePerUnit
                            price={mainOffer.price}
                            unit={product.price_unit}
                            factor={product.price_factor}
                            hasPricePerFactor={hasPricePerFactor}
                          />
                        )}

                      {Boolean(sgrTax?.value) && (
                        <span className="text-gray-1200 text-xs leading-none inline-block mt-[6px]">
                          Taxa garantie returnare: {sgrTax.value} lei
                        </span>
                      )}

                      {hasFinancingOptions && (
                        <ProductFinancing
                          product={product}
                          label="productPage"
                          simulation={
                            loanSimulation?.[
                              product.installments_provider || "credex"
                            ]
                          }
                        />
                      )}
                    </div>
                  )}

                  <div className="w-full">
                    <Status product={product} />

                    {mainOffer && !isLocalSeller && (
                      <ProductPageSellerInfo seller={mainOffer.seller} />
                    )}
                  </div>
                </div>

                {hasResealed && (
                  <ProductPageResealedInfo
                    resealedList={product.resealed}
                    onResealedClick={onResealedClick}
                  />
                )}

                {hasProductVariations && (
                  <ProductVariations product={product} />
                )}

                {!isSaleable && hasResealed && !hasStorePickup && (
                  <ProductButtonViewResealed href="#resigilate" />
                )}

                <div className="flex flex-col items-center space-y-2 !mt-5 md:mt-0 lg:flex-row lg:space-y-0 lg:space-x-4">
                  <div
                    className={classNames("w-full", {
                      "lg:w-3/4": hasPricePerFactor,
                      "lg:w-1/2": !hasPricePerFactor,
                    })}
                    ref={addToCartRef}
                  >
                    <AddToCart
                      product={product}
                      activeServices={activeServices}
                      westaco={westacoData}
                      disabled={
                        (isWestaco && !westacoValid) ||
                        (!isSaleable && !hasStorePickup)
                      }
                      noMargin
                      isInSpecialCampaign={isInSpecialCampaign}
                    />
                  </div>
                </div>

                {hasStorePickup && (
                  <ProductStoreAvailability identifier={product.sku} />
                )}

                {isLockerEligible && <ProductLockerAvailability />}
              </div>

              {/* product services */}
              <div className="mt-[24px] space-y-[24px]">
                {shippingInfo?.length > 0 && (
                  <ProductPageShipping
                    shippingInfo={shippingInfo}
                    hasFreeShippingPromo={hasFreeShippingPromo}
                  />
                )}

                {credexConfig?.cashbackBanner?.enabled &&
                  credexConfig?.cashbackBanner?.label &&
                  !isWestaco && (
                    <div className="text-[14px]">
                      <CredexCashbackBanner />
                    </div>
                  )}

                {hasServices && (
                  <>
                    <ProductServices
                      services={product.product_links}
                      activeServices={activeServices}
                      removeService={removeService}
                      serviceClick={serviceClick}
                    />

                    <ProductServicesModal
                      services={product.product_links}
                      activeServices={activeServices}
                      serviceClick={serviceClick}
                    />
                  </>
                )}

                <div
                  className={classNames("flex flex-col gap-[24px]", {
                    "md:flex-row-reverse": hasBenefits,
                  })}
                >
                  {hasBenefits && (
                    <div className="bg-[#F5F5F4] p-[8px] rounded h-fit w-full md:max-w-[50%]">
                      <div
                        htmlFor="toggle-benefits"
                        className="flex items-center justify-between cursor-pointer"
                        onClick={toggleBenefits}
                      >
                        <div className="flex items-center gap-[8px]">
                          <Heading
                            title="Extra beneficii"
                            as="h2"
                            display="h5"
                            classesProp="font-medium"
                            sameSizeOnMobile={true}
                          />

                          {labels.map(({ title, image }) => (
                            <ImageWithFallback
                              key={title}
                              src={image ? `${settings.cdn}/${image}` : null}
                              className={classNames(
                                "max-w-[20px] h-auto transition-all duration-300",
                                {
                                  "h-0": showBenefits,
                                }
                              )}
                              fallback={`${settings.websiteStatics}/images/benefits-placeholder.svg`}
                            />
                          ))}
                        </div>

                        <div
                          className={classNames(
                            "transition-transform duration-300",
                            {
                              "rotate-180": showBenefits,
                            }
                          )}
                        >
                          <ArrowDown />
                        </div>
                      </div>

                      <div
                        className={classNames(
                          "overflow-hidden transition-all duration-300",
                          {
                            "h-auto pt-[8px]": showBenefits,
                            "h-0": !showBenefits,
                          }
                        )}
                      >
                        <ProductBenefits benefits={labels} />
                      </div>
                    </div>
                  )}

                  <div
                    className={classNames(
                      "flex flex-col w-full md:w-[50%] gap-[16px]",
                      {
                        "flex-wrap": hasBenefits,
                      }
                    )}
                  >
                    <ProductPageWishlistAndCompare product={product} />
                  </div>
                </div>

                {hasCredexUserPromo && (
                  <CredexUserPromoBanner
                    layout="extraInfo"
                    installmentValue={installmentValue}
                  />
                )}
              </div>

              {isWestaco && (
                <Westaco
                  options={mainOffer.westaco_options}
                  callback={westacoFormCb}
                />
              )}
            </>
          )}
        </div>
      </div>

      {hasOtherOffers && (
        <div id="oferte" data-test="PRODUCT_OFFERS">
          <ProductOffers
            offers={otherOffers}
            product={product}
            getIsInSpecialCampaign={getIsInSpecialCampaign}
            getLabelsBySellerId={getLabelsBySellerId}
          />
        </div>
      )}

      {hasBundles && (
        <div
          id="pachete"
          className="mt-27px pb-22px md:mt-15px"
          data-test="PRODUCT_BUNDLES"
          ref={bundlesRef}
        >
          <Heading
            as="h2"
            display="h2"
            title="Cumpara impreuna"
            border="bottom"
            classesProp="mb-4"
          />

          <Bundles productBundles={productBundles} ref={bundlesRef} />
        </div>
      )}

      {hasResealed && (
        <div id="resigilate">
          <ResealedList product={product} key={product.sku ?? ""} />
        </div>
      )}

      {/* {settings.websiteName === "altex" && !hasBundles && (
        <div
          id="pachete"
          className="relative w-screen bg-white inset-x-1/2	-ml-[50vw] -mr-[50vw]"
        >
          <div className="mx-auto max-w-[1240px]">
            // XXX Don't remove class swogo-bundle-1, it's needed for product bundling
            <div id="swogo-bundle-1" className="swogo-box mb-12" />
          </div>
        </div>
      )} */}

      {productRecommendations && (
        <>
          {!isWestaco && hasProductAccesories && (
            <div id="accessories" className="lg:py-5 py-3">
              <Heading
                as="h2"
                display="h2"
                title="Accesorii"
                border="bottom"
                classesProp="mb-4"
              />

              {/* TODO: It seems that the tabs component has a md:container class
              that has a horizontal padding that isn't needed here. */}
              <div className="md:mx-[-15px]">
                <Tabs>
                  {productRecommendations.cross_sell_tabs.map(
                    ({ title, products }, index) => (
                      <Tab selected={index === 0} key={index} title={title}>
                        <ExpandableProductList
                          externalTracking
                          key={currentProduct.product.sku ?? ""}
                          products={products.map((product, index) => ({
                            ...product,
                            index: index + 1,
                            item_list_name: "accesorii",
                          }))}
                          desktopVisibleProducts={5}
                          onProductClick={(product) =>
                            handleProductClick(product)
                          }
                        />
                      </Tab>
                    )
                  )}
                </Tabs>
              </div>
            </div>
          )}
          {productRecommendations.up_sell?.length > 0 && (
            <div className="lg:pt-4 lg:pb-5 py-3">
              <Heading
                as="h2"
                display="h2"
                title="Produse similare"
                border="bottom"
                classesProp="mb-4"
              />

              <ExpandableProductList
                key={currentProduct.product.sku ?? ""}
                externalTracking
                products={productRecommendations.up_sell.map(
                  (product, index) => ({
                    ...product,
                    index: index + 1,
                    item_list_name: "produse similare",
                  })
                )}
                onProductClick={(product) => handleProductClick(product)}
              />
            </div>
          )}
        </>
      )}

      {isWestaco ? (
        <div id="description">
          <ProductDescription
            staticHTML={product.description ?? ""}
            contentEditorBlock={product.description_editor_object ?? ""}
            cmsBlocks={productDescriptionCMSBlocks}
          />
        </div>
      ) : (
        <div className="space-y-2 py-2 divide-y divide-black/30 border-y mb-6">
          <Accordion
            expandedItems={openedAccordionItems}
            onToggle={onAccordionItemToggle}
          >
            <AccordionItem id="description">
              <AccordionItemHeader>
                <Heading title="Descriere" />
              </AccordionItemHeader>

              <AccordionItemBody>
                <ProductDescription
                  // TODO: Re-check if product.use_external_description returns INT
                  // If true, remove strict equality check
                  useExternalContentProvider={
                    product.use_external_description === 1
                  }
                  staticHTML={product.description ?? ""}
                  contentEditorBlock={product.description_editor_object ?? ""}
                  cmsBlocks={productDescriptionCMSBlocks}
                  ean={product.ean_codes?.split(",")[0] || ""}
                  productNumber={productNumber}
                  brand={product.brand_name || ""}
                />

                {(hasWarranty() || hasGreenTax) && (
                  <div className="flex flex-col space-y-2 md:flex-row md:space-y-0 md:space-x-4 items-start md:items-center col-span-2 border-t border-[#999999]/60 py-2.5">
                    {hasWarranty() && <ProductWarranty data={warranty} />}

                    {hasGreenTax && (
                      <ProductGreenTax data={mainOffer.price.green_tax} />
                    )}
                  </div>
                )}
              </AccordionItemBody>
            </AccordionItem>

            <AccordionItem id="additional">
              <AccordionItemHeader>
                <Heading title="Specificatii" />
              </AccordionItemHeader>

              <AccordionItemBody>
                <Specifications groups={product.info.groups} />
              </AccordionItemBody>
            </AccordionItem>

            <AccordionItem id="reviews" isPreRendered={false}>
              <AccordionItemHeader>
                <div className="flex items-center space-x-2.5">
                  <Heading as="h2" display="h2" title="Recenzii" />

                  <Ratings
                    reviewsCount={reviewsCount}
                    ratingSummary={reviewsScore}
                  />

                  {reviewsScore > 0 && reviewsCount > 0 && (
                    <span className="text-2xl font-medium mx-10px lg:mr-7px">
                      {reviewAverage}
                    </span>
                  )}

                  <span className="py-1 px-2 bg-yellow rounded-md text-sm leading-none">
                    {reviewsCount}
                  </span>
                </div>
              </AccordionItemHeader>

              <AccordionItemBody>
                <ReviewsContainer
                  product={product}
                  type="reviews"
                  reviewsSummary={currentProduct.reviews_summary?.items?.[0]}
                />
              </AccordionItemBody>
            </AccordionItem>

            <AccordionItem id="questions" isPreRendered={false}>
              <AccordionItemHeader>
                <div className="flex items-center space-x-2.5">
                  <Heading title="Intrebari" />

                  <span className="py-1 px-2 bg-yellow rounded-md text-sm leading-none">
                    {questionsCount}
                  </span>
                </div>
              </AccordionItemHeader>

              <AccordionItemBody>
                <ReviewsContainer
                  product={product}
                  type="questions"
                  reviewsSummary={currentProduct?.reviews_summary?.items?.[0]}
                />
              </AccordionItemBody>
            </AccordionItem>

            <AccordionItem id="compare" isPreRendered={false}>
              <AccordionItemHeader>
                <Heading title="Compara" />
              </AccordionItemHeader>

              <AccordionItemBody>
                <ProductCompareSection product={product} />
              </AccordionItemBody>
            </AccordionItem>
          </Accordion>
        </div>
      )}
      {productRecommendations && (
        <>
          {productRecommendations.also_bought?.length > 0 && (
            <div className="lg:pt-4 lg:pb-5 py-3">
              <Heading
                as="h2"
                display="h2"
                title="Alti vizitatori au cumparat"
                border="bottom"
                classesProp="mb-4"
              />

              <ExpandableProductList
                externalTracking
                key={currentProduct.product.sku ?? ""}
                products={productRecommendations.also_bought.map(
                  (product, index) => ({
                    ...product,
                    index: index + 1,
                    item_list_name: "alti vizitatori au cumparat",
                  })
                )}
                onProductClick={(product) => handleProductClick(product)}
              />
            </div>
          )}

          {!isWestaco && productRecommendations.bought_together?.length > 0 && (
            <div className="lg:pt-4 lg:pb-5 py-3">
              <Heading
                as="h2"
                display="h2"
                title="Cumparate frecvent impreuna"
                border="bottom"
                classesProp="mb-4"
              />

              <ExpandableProductList
                externalTracking
                key={currentProduct.product.sku ?? ""}
                products={productRecommendations.bought_together.map(
                  (product, index) => ({
                    ...product,
                    index: index + 1,
                    item_list_name: "cumparate frecvent impreuna",
                  })
                )}
                desktopVisibleProducts={5}
                onProductClick={(product) => handleProductClick(product)}
              />
            </div>
          )}
        </>
      )}
      <div className="lg:pt-4 lg:pb-5 py-3">
        <ProductsRecentlyViewed
          key={product.sku ?? ""}
          visibleProductSku={product.sku}
          onProductClick={handleProductClick}
        />
      </div>
      <CompareWidget
        showsStickyCta={
          !isEol &&
          isVisible &&
          (isSaleable || hasStorePickup) &&
          !isStickyFooterVisible
        }
      />
    </div>
  );
};

export default connect(
  (state) => ({
    currentProduct: state.catalog.currentProduct,
    acc: state.catalog.currentCategory,
    hash: state.navigation.hash,
    cmsBlocks: state.app.cmsBlocks,
    user: state.user,
    cart: state.cart,
    credexConfig: state.credex.data,
    lockers: state.stores.lockers,
  }),
  {
    apiCall,
    trackProductClick,
    addProductToProductNavigationHistory,
    addModal,
    getAddresses,
  }
)(CatalogView);
